const PENDING_RECORDS_KEY = "alraheem:pending-records";
const PENDING_EVENT = "alraheem:pending-records-change";
const isBrowser = typeof window !== "undefined";

export interface PendingRecord {
  id: string; // Local ID (UUID)
  type: "goat" | "feedPurchase" | "expense" | "booking";
  data: any; // The record data
  timestamp: number;
  endpoint: string;
  method: string;
  body?: string; // Serialized request body for sync
  headers?: Record<string, string>;
  imageData?: string; // Base64 encoded image for goats (if FormData)
  retryCount?: number; // Number of retry attempts
  lastRetryAt?: number; // Timestamp of last retry attempt
  lastError?: string; // Last error message if sync failed
}

const readPendingRecords = (): PendingRecord[] => {
  if (!isBrowser) {
    return [];
  }

  try {
    const raw = localStorage.getItem(PENDING_RECORDS_KEY);
    if (!raw) {
      return [];
    }
    const parsed = JSON.parse(raw) as PendingRecord[];
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    console.warn("[pending-records] Failed to read pending records", error);
    return [];
  }
};

let cachedPendingRecords: PendingRecord[] = readPendingRecords();

const setCache = (records: PendingRecord[]) => {
  cachedPendingRecords = records;
};

export const writePendingRecords = (records: PendingRecord[]) => {
  if (!isBrowser) {
    return;
  }

  try {
    // Update cache first, then localStorage, then dispatch event
    setCache(records);
    localStorage.setItem(PENDING_RECORDS_KEY, JSON.stringify(records));
    
    // Verify write was successful
    const verify = localStorage.getItem(PENDING_RECORDS_KEY);
    if (verify) {
      const parsed = JSON.parse(verify);
      if (parsed.length !== records.length) {
        console.warn(`[pending-records] Write verification failed. Expected ${records.length}, got ${parsed.length}`);
      }
    }
    
    const event = new CustomEvent(PENDING_EVENT, { detail: { records } });
    window.dispatchEvent(event);
    console.log(`[pending-records] Updated pending records. Count: ${records.length}`);
  } catch (error) {
    console.warn("[pending-records] Failed to write pending records", error);
  }
};

export const getPendingRecordsSnapshot = (forceRefresh = false): PendingRecord[] => {
  if (forceRefresh) {
    const fresh = readPendingRecords();
    setCache(fresh);
    return fresh;
  }
  return cachedPendingRecords;
};

export const getPendingRecordsByType = (type: PendingRecord["type"]): PendingRecord[] => {
  return cachedPendingRecords.filter((record) => record.type === type);
};

export const subscribePendingRecords = (callback: () => void) => {
  if (!isBrowser) {
    return () => void 0;
  }

  const handler = (event: Event) => {
    const customEvent = event as CustomEvent<{ records?: PendingRecord[] }>;
    if (customEvent.detail?.records) {
      setCache(customEvent.detail.records);
    } else {
      setCache(readPendingRecords());
    }
    callback();
  };
  window.addEventListener(PENDING_EVENT, handler);
  return () => window.removeEventListener(PENDING_EVENT, handler);
};

export const addPendingRecord = (
  type: PendingRecord["type"],
  data: any,
  endpoint: string,
  method: string,
  body?: string,
  headers?: Record<string, string>,
  imageData?: string
): PendingRecord => {
  if (!isBrowser) {
    throw new Error("Cannot add pending record outside browser");
  }

  // Always read fresh to avoid duplicates
  const records = readPendingRecords();
  
  // Create a unique fingerprint to detect duplicates
  // For goats, use breed + ownershipType + user (if in_care) or purchaseSupplierName (if own)
  const createFingerprint = (recordData: any, recordType: string, recordEndpoint: string): string => {
    if (recordType === "goat") {
      // Use key fields that would make it a duplicate
      const key = `${recordData.breed || ""}_${recordData.ownershipType || ""}_${recordData.user || recordData.purchaseSupplierName || ""}_${recordData.name || ""}`;
      return key.toLowerCase().trim();
    }
    // For other types, use endpoint + key data fields
    if (recordType === "expense") {
      return `${recordEndpoint}_${recordData.description || ""}_${recordData.amount || ""}_${recordData.expenseDate || ""}`.toLowerCase().trim();
    }
    if (recordType === "feedPurchase") {
      return `${recordEndpoint}_${recordData.feedName || ""}_${recordData.feedType || ""}_${recordData.purchaseDate || ""}`.toLowerCase().trim();
    }
    // Generic fallback
    return `${recordEndpoint}_${body?.substring(0, 100) || ""}`.toLowerCase().trim();
  };

  const newFingerprint = createFingerprint(data, type, endpoint);
  
  // Check for duplicates within last 30 seconds (more lenient to prevent accidental duplicates)
  const recentThreshold = Date.now() - 30000;
  const isDuplicate = records.some((r) => {
    if (r.type !== type || r.endpoint !== endpoint) return false;
    if (r.timestamp < recentThreshold) return false;
    const existingFingerprint = createFingerprint(r.data, r.type, r.endpoint);
    return existingFingerprint === newFingerprint;
  });

  if (isDuplicate) {
    console.warn(`[pending-records] Duplicate record detected within 30s, skipping add. Fingerprint: ${newFingerprint}`);
    throw new Error("Duplicate record detected. This record is already queued for sync.");
  }

  const record: PendingRecord = {
    id: crypto.randomUUID(),
    type,
    data: {
      ...data,
      _id: `pending-${crypto.randomUUID()}`, // Temporary ID for UI
      isPending: true, // Flag to identify pending records
      pendingId: crypto.randomUUID(), // Store the local ID
    },
    timestamp: Date.now(),
    endpoint,
    method,
    body,
    headers,
    imageData,
  };
  records.push(record);
  writePendingRecords(records);
  console.log(`[pending-records] Added pending record ${record.id} of type ${type}`);
  return record;
};

export const removePendingRecord = (id: string): boolean => {
  if (!isBrowser) {
    return false;
  }

  // Always read fresh from localStorage to avoid stale cache
  const records = readPendingRecords();
  const beforeCount = records.length;
  const filtered = records.filter((record) => record.id !== id);
  
  if (filtered.length < beforeCount) {
    // Record was found and removed
    writePendingRecords(filtered);
    
    // Verify removal was successful
    const verify = readPendingRecords();
    const stillExists = verify.some((r) => r.id === id);
    
    if (stillExists) {
      console.error(`[pending-records] Failed to remove record ${id}, still exists after removal attempt`);
      // Try again with fresh read
      const retryRecords = readPendingRecords();
      const retryFiltered = retryRecords.filter((record) => record.id !== id);
      writePendingRecords(retryFiltered);
      return retryFiltered.length < retryRecords.length;
    }
    
    console.log(`[pending-records] Successfully removed pending record ${id} from storage`);
    return true;
  } else {
    console.warn(`[pending-records] Record ${id} not found in pending records (may have been already removed)`);
    return false;
  }
};

export const clearPendingRecords = (): void => {
  if (!isBrowser) {
    return;
  }
  writePendingRecords([]);
};

// Helper to convert FormData to JSON for storage
export const formDataToJson = async (formData: FormData): Promise<{ data: Record<string, any>; imageData?: string }> => {
  const data: Record<string, any> = {};
  let imageData: string | undefined;

  for (const [key, value] of formData.entries()) {
    if (key === "image" && value instanceof File) {
      // Convert image to base64 for storage
      imageData = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(value);
      });
    } else {
      data[key] = value;
    }
  }

  return { data, imageData };
};

// Helper to convert stored JSON back to FormData for sync
export const jsonToFormData = (data: Record<string, any>, imageData?: string): FormData => {
  const formData = new FormData();

  for (const [key, value] of Object.entries(data)) {
    if (value !== undefined && value !== null) {
      formData.append(key, String(value));
    }
  }

  if (imageData) {
    try {
      // Parse base64 data URL - handle both data:image/...;base64,... and plain base64
      let mimeType = "image/jpeg"; // default
      let base64Data = imageData;

      if (imageData.startsWith("data:")) {
        const base64Match = imageData.match(/^data:([^;]+);base64,(.+)$/);
        if (base64Match) {
          mimeType = base64Match[1];
          base64Data = base64Match[2];
        } else {
          // Try without base64 prefix
          const simpleMatch = imageData.match(/^data:([^,]+),(.+)$/);
          if (simpleMatch) {
            mimeType = simpleMatch[1];
            base64Data = simpleMatch[2];
          } else {
            console.warn("[jsonToFormData] Could not parse data URL format, using default");
          }
        }
      }

      // Validate base64 data
      if (!base64Data || base64Data.length === 0) {
        console.error("[jsonToFormData] Empty base64 data");
        return formData;
      }

      // Convert base64 to binary
      const byteString = atob(base64Data);
      if (byteString.length === 0) {
        console.error("[jsonToFormData] Failed to decode base64 data");
        return formData;
      }

      const ab = new ArrayBuffer(byteString.length);
      const ia = new Uint8Array(ab);
      for (let i = 0; i < byteString.length; i++) {
        ia[i] = byteString.charCodeAt(i);
      }

      // Determine file extension from MIME type
      const extensionMap: Record<string, string> = {
        "image/jpeg": "jpg",
        "image/jpg": "jpg",
        "image/png": "png",
        "image/gif": "gif",
        "image/webp": "webp",
      };
      const extension = extensionMap[mimeType] || "jpg";

      // Create File with proper MIME type and filename
      const blob = new Blob([ab], { type: mimeType });
      const filename = `goat-image-${Date.now()}.${extension}`;
      const file = new File([blob], filename, { 
        type: mimeType,
        lastModified: Date.now()
      });

      // Validate file was created correctly
      if (file.size === 0) {
        console.error("[jsonToFormData] Created file has zero size");
        return formData;
      }

      // Append file to FormData - multer expects the field name to be "image"
      // Don't pass filename as third parameter to avoid issues with some FormData implementations
      formData.append("image", file);
      console.log(`[jsonToFormData] Successfully converted base64 to File: ${filename}, type: ${mimeType}, size: ${file.size} bytes`);
      
      // Verify the file was added to FormData
      const verifyFile = formData.get("image");
      if (!verifyFile) {
        console.error("[jsonToFormData] File was not added to FormData correctly");
        return formData;
      }
    } catch (error) {
      console.error("[jsonToFormData] Failed to convert base64 image to File:", error);
      // Don't append image if conversion fails, but still return formData with other fields
    }
  }

  return formData;
};
